import type { QueryFunctionContext } from '@tanstack/react-query'
import { useInfiniteQuery, useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import type { AxiosError, AxiosResponse } from 'axios'
import { generatePath } from 'react-router-dom'

import api from '../../api'
import i18n from '@src/i18n'
import { toast } from '@helpers/toaster'
import { AccountingKeys, RequestsKeys, OrgStructureKeys } from '../Keys'

import { useGetOne, useCreate, usePatch, useGetList } from '../request'

import type {
  ApprovalRequestList,
  ApprovalRequest,
  Filter,
  ExportFilter,
  FiltersList,
  RequestPayload,
  ApproveRejectRequest,
  ApproveRejectResponse,
  RequestStepUpdateRequest,
  RequestStepUpdateResponse,
  ApproverAdjustment,
  Comment,
  BulkApproveRejectRequest,
  ApprovalRequestVersionData,
  CommentRequest,
  PublishRequest,
  AutoDecisionRequest,
  Tag,
  TagPayload,
} from '@models/Request'

export { RequestsKeys }

const PATHS = {
  requests: '/approval-requests/',
  sendNow: '/approval-requests/:id/send',
  publish: '/approval-requests/:id/publish',
  bulk: '/approval-requests/bulk',
  filters: '/approval-requests/filters',
  fieldValues: '/approval-requests/field-values',
  export: '/approval-requests/export',
  comments: '/approval-requests/:id/comments',
  remind: '/approval-requests/:id/remind',
  autoDecision: '/approval-requests/:id/auto-decision/:decision',
  forward: '/approval-requests/:id/forward',
  tags: '/approval-requests/tags',
  accountingSyncRetry: '/approval-requests/:id/accounting-sync/retry',
}

export interface AccountingSyncRetryResult {
  stage_id: number
  outbound_integration_type: string
  status: string
  error_message: string | null
}

export interface AccountingSyncRetryResponse {
  results: AccountingSyncRetryResult[]
}

export const useGetRequests = (filter?: Partial<Filter>, enabled = true) => {
  // When no workflow filter is set, the table only renders base columns —
  // none of which read from the per-row `data` payload. Tell the backend
  // to omit `data` to keep the default-view response small. Once the user
  // selects a workflow, workflow-specific field columns become available
  // (and field-value/date filters), so we drop the flag and refetch.
  const lite = filter?.workflow ? undefined : 'true'
  const params = { ...(filter || {}), lite }

  return useGetOne<ApprovalRequestList>([RequestsKeys.Requests, params], PATHS.requests, {
    query: {
      enabled,
      // A filter change is a new query key, so without this the table drops to
      // `undefined` and blanks out for the whole round trip — which on a slow
      // filter is tens of seconds of empty screen. Hold the previous page and
      // let the consumer dim it via `isPreviousData`, the same way the
      // Pipelines processes list does.
      keepPreviousData: true,
      staleTime: 30 * 1000,
      // Keep cache for 30 min so returning to the tab after a long idle shows
      // cached rows immediately, instead of blanking the table when the default
      // 5-min cacheTime would have evicted.
      cacheTime: 30 * 60 * 1000,
      // No real-time channel for ARs and the table is effectively static between
      // user actions — every mutation that can change list data (approve/reject/
      // retry/edit/tag/...) already invalidates [RequestsKeys.Requests], so a
      // refresh after acting still happens. Disable focus-refetch to stop the
      // network chatter from casual tab-switching.
      refetchOnWindowFocus: false,
    },
    axios: {
      params,
    },
  })
}
export const useInfiniteGetRequestsByStatus = (filter?: Filter) => {
  const onLoadMore = async (ctx: QueryFunctionContext) => {
    const { data }: AxiosResponse = await api.get(PATHS.requests, {
      params: {
        ...filter,
        page: ctx.pageParam || 1,
      },
    })
    return { rows: data?.results, count: data.count }
  }

  const { data, isFetchingNextPage, fetchNextPage, hasNextPage, refetch } = useInfiniteQuery(
    [RequestsKeys.Requests, filter?.status],
    (ctx) => onLoadMore(ctx),
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.count <= (filter?.size || 0) * allPages.length ? null : allPages.length
      },
      staleTime: 30 * 1000,
      // Matches `useGetRequests` — mutations invalidate this cache, so a
      // refresh after acting still happens; we just stop the network chatter
      // from casual tab-switching.
    },
  )
  return { data, isFetchingNextPage, fetchNextPage, hasNextPage, refetch }
}

export const useGetRequest = (id?: number | string) => {
  return useGetOne<ApprovalRequest>([RequestsKeys.Request, id], `${PATHS.requests}${id}`, {
    query: {
      enabled: !!id,
    },
  })
}

export const useGetRequestsByIds = (ids?: Array<number>) => {
  return useQueries({
    queries:
      ids?.map((id) => {
        return {
          queryKey: [RequestsKeys.Request, id],
          staleTime: Infinity,
          queryFn: async () => (await api.get<ApprovalRequest>(`${PATHS.requests}${id}`)).data,
        }
      }) || [],
  })
}

export const useGetRequestData = (id?: number | string, versionId?: number | string) => {
  return useGetOne<ApprovalRequestVersionData>(
    [RequestsKeys.Request, id, versionId],
    `${PATHS.requests}${id}/data/${versionId}`,
    {
      query: {
        enabled: !!id && !!versionId,
        staleTime: Infinity,
      },
    },
  )
}

// for one time fetching reasons
export const useFetchRequest = () => {
  return useMutation<ApprovalRequest, unknown, number | string>(async (id: number | string) => {
    const res = await api.get<ApprovalRequest>(`${PATHS.requests}${id}`)
    return res.data
  })
}

export const useValidateRequest = (id?: number | string, uuid?: string) => {
  return useGetOne<unknown>([RequestsKeys.RequestValidation, uuid], `${PATHS.requests}${id}/validate/${uuid}`, {
    query: {
      initialData: {},
      enabled: !!id && !!uuid,
    },
  })
}

export const useGetRequestsFilters = (filterContext?: Record<string, unknown>) => {
  // Strip empty values so the cache key stays stable across no-op changes.
  const cleanContext = filterContext
    ? Object.fromEntries(
        Object.entries(filterContext).filter(
          ([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0),
        ),
      )
    : {}
  return useGetOne<FiltersList>([RequestsKeys.Filters, cleanContext], PATHS.filters, {
    query: {
      // Non-suspending. Earlier this was `suspense: !hasContext` so the
      // initial load got a clean transition — but suspense propagates
      // axios errors to the nearest ErrorBoundary, which blew up the
      // entire approval-requests page when this endpoint 403'd (e.g. a
      // role that grants request.view but not the filters endpoint, or
      // a transient 403 after a token rotation). The Filters component
      // already renders an empty state when data is undefined, so the
      // first-paint cost is negligible compared to the page-crash risk.
      keepPreviousData: true,
      staleTime: 30 * 1000,
      // Filter options (workflows, requestors, statuses, tags, …) change
      // rarely; the relevant mutations (createWorkflow, createTag, …)
      // already invalidate [RequestsKeys.Filters], so a fresh dropdown after
      // acting still happens. Falling back to the global `false` default
      // stops casual tab-switching from re-fetching this on every focus.
    },
    axios: {
      params: cleanContext,
    },
  })
}

export const useGetFieldValues = (workflowId?: string | number | null) => {
  return useGetOne<Record<string, { value: string; label: string }[]>>(
    [RequestsKeys.Filters, 'field-values', workflowId],
    PATHS.fieldValues,
    {
      query: {
        enabled: !!workflowId,
      },
      axios: {
        params: { workflow: workflowId },
      },
    },
  )
}

export const useRetryAccountingSync = () => {
  const cache = useQueryClient()

  return useMutation<AccountingSyncRetryResponse, AxiosError<{ detail?: string }>, { id: number | string }>(
    async ({ id }) => {
      const path = PATHS.accountingSyncRetry.replace(':id', String(id))
      const res = await api.post<AccountingSyncRetryResponse>(path)
      return res.data
    },
    {
      // Shared mutationKey de-duplicates rapid double-clicks on the same row.
      mutationKey: [RequestsKeys.RetryAccountingSync],
      onSuccess: () => {
        // Invalidate every AR-detail cache, not just `[RequestsKeys.Request, id]`.
        // The detail page caches under the AR's `pipeline_approval_request_number`
        // (driven by useParams) while the retry button passes the numeric AR id
        // — so a key-specific invalidation misses the open detail cache and the
        // badge stays "failed" until manual reload. Broader invalidation is
        // safe: only one detail page is realistically open at a time.
        cache.invalidateQueries([RequestsKeys.Request])
        cache.invalidateQueries([RequestsKeys.Requests])
      },
    },
  )
}

export const useApproveReject = () => {
  const cache = useQueryClient()

  return useMutation<ApproveRejectResponse, AxiosError<ApproveRejectResponse>, ApproveRejectRequest>(
    async ({ status, id, comment, uuid, signature }: ApproveRejectRequest) => {
      const path = `${PATHS.requests}${uuid ? 'p/' : ''}${uuid || id}/${status}`
      const res = await api.patch<ApproveRejectResponse>(path, { comment: comment || null, signature })
      return res.data
    },
    {
      mutationKey: [RequestsKeys.ApproveReject],
      onSuccess: () => {
        // Invalidate every AR-detail cache, not just `[RequestsKeys.Request, id]`.
        // The detail page caches under `pipeline_approval_request_number` (via
        // useParams) while some call sites (dashboard card, bulk path) pass
        // the numeric AR id — so a key-specific invalidation can miss the
        // open detail cache. Broader invalidation matches the precedent at
        // useRetryAccountingSync; only one detail page is realistically open.
        cache.invalidateQueries([RequestsKeys.Request])
        cache.invalidateQueries([RequestsKeys.Requests])
        cache.invalidateQueries([OrgStructureKeys.OrgStructure])
        cache.removeQueries({ queryKey: [AccountingKeys.Contacts], type: 'inactive' })
      },
      // Refetch on failure so a stale page (e.g. the requestor edited and
      // reset approvals between page load and click) re-renders without the
      // now-invalid action buttons. Pairs with APRQ.BL.106 on the backend.
      onError: () => {
        cache.invalidateQueries([RequestsKeys.Request])
        cache.invalidateQueries([RequestsKeys.Requests])
      },
    },
  )
}

export const useAutoDecision = () => {
  const cache = useQueryClient()

  return useMutation<ApproveRejectResponse, AxiosError<ApproveRejectResponse>, AutoDecisionRequest>(
    async ({ decision, id }: AutoDecisionRequest) => {
      const path = `${PATHS.autoDecision.replace(':id', id).replace(':decision', decision)}`
      const res = await api.patch<ApproveRejectResponse>(path)
      return res.data
    },
    {
      mutationKey: [RequestsKeys.ApproveReject],
      onSuccess: (_, variables) => {
        cache.invalidateQueries([RequestsKeys.Request, variables.id])
        cache.invalidateQueries([RequestsKeys.Requests])
        cache.invalidateQueries([OrgStructureKeys.OrgStructure])
        cache.removeQueries({ queryKey: [AccountingKeys.Contacts], type: 'inactive' })
      },
    },
  )
}

export const usePublishRequest = () => {
  const cache = useQueryClient()

  return useMutation<ApproveRejectResponse, AxiosError<ApproveRejectResponse>, PublishRequest>(
    async ({ id }: PublishRequest) => {
      const res = await api.patch<ApproveRejectResponse>(PATHS.publish.replace(':id', id?.toString() || ''))
      return res.data
    },
    {
      mutationKey: [RequestsKeys.PublishRequest],
      onSuccess: (_, variables) => {
        cache.invalidateQueries([RequestsKeys.Request, variables.id])
        cache.invalidateQueries([RequestsKeys.Requests])
      },
    },
  )
}

export const useBulkApproveReject = () => {
  const cache = useQueryClient()
  return useMutation<ApproveRejectResponse[], AxiosError<BulkApproveRejectRequest>, BulkApproveRejectRequest>(
    async ({ ids, status }) => {
      const path = `${PATHS.bulk}/${status}`
      const res = await api.patch(path, { ids })

      return res.data
    },
    {
      mutationKey: [RequestsKeys.ApproveReject],
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Requests])
        cache.removeQueries({ queryKey: [AccountingKeys.Contacts], type: 'inactive' })
      },
    },
  )
}

export const usePublicComment = (uuid: string) => {
  return useCreate<{ text: string }>(`${PATHS.requests}p/${uuid}/comment`)
}

export const useGetPublicInfo = (uuid: string) => {
  return useGetOne<
    {
      id: number
      status: string
      actions: { reject: string; approve: string }
    },
    {
      id: number
      status: string
    }
  >([RequestsKeys.PublicRequest], `${PATHS.requests}p/${uuid}`)
}

export const useCreateRequest = () => {
  const cache = useQueryClient()
  return useCreate<RequestPayload, ApprovalRequest>(PATHS.requests, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Requests])
      },
    },
  })
}

export const useUpdateRequest = (requestId?: string | number) => {
  const cache = useQueryClient()
  return usePatch<RequestPayload, ApprovalRequest>(`${PATHS.requests}${requestId}`, {
    mutation: {
      onSuccess: () => {
        cache.removeQueries([RequestsKeys.Request, requestId])
        cache.invalidateQueries([RequestsKeys.Requests])
      },
    },
  })
}
export const useSendScheduledRequestNow = (requestId: string | number) => {
  const cache = useQueryClient()
  return usePatch(PATHS.sendNow.replace(':id', requestId?.toString()), {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Request, requestId])
        cache.invalidateQueries([RequestsKeys.Requests])
      },
    },
  })
}

// Turn a backend approver-adjustment into user-facing copy. Editing approvers
// can silently substitute an on-vacation pick for their stand-in, or drop a
// duplicate — a bare 200 hides that, so we toast what actually happened.
const approverAdjustmentMessage = (adjustment: ApproverAdjustment): string => {
  const { submitted_name: name, routed_to_name: substitute, reason } = adjustment
  switch (reason) {
    case 'vacation_substitute':
      return i18n.t('request:step.approverAdjusted.vacationSubstitute', { name, substitute })
    case 'vacation_substitute_duplicate':
      return i18n.t('request:step.approverAdjusted.vacationSubstituteDuplicate', { name, substitute })
    case 'duplicate':
    default:
      return i18n.t('request:step.approverAdjusted.duplicate', { name })
  }
}

export const useUpdateRequestSteps = (id: string | number) => {
  const cache = useQueryClient()
  return usePatch<RequestStepUpdateRequest, RequestStepUpdateResponse>(`${PATHS.requests}${id}/approvers`, {
    mutation: {
      onSuccess: (data) => {
        cache.invalidateQueries([RequestsKeys.Request, id])
        cache.invalidateQueries([RequestsKeys.Requests])
        // Surface any silent substitution/dedupe the backend applied.
        data?.adjustments?.forEach((adjustment) => toast.info(approverAdjustmentMessage(adjustment)))
      },
    },
  })
}

export const useExport = (params: ExportFilter) => {
  return useMutation(
    async () => {
      const response = await api.get(PATHS.export, {
        params: Object.keys(params).reduce(
          (acc, key) =>
            params[key as keyof ExportFilter] ? { ...acc, [key]: params[key as keyof ExportFilter] } : acc,
          {},
        ),
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `approval_requests_${new Date().toISOString()}.${params.type}`)
      document.body.appendChild(link)
      link.click()

      return null
    },
    {
      mutationKey: [RequestsKeys.Export],
    },
  )
}

export const useSendComment = (id?: string | number) => {
  const cache = useQueryClient()
  return useCreate<CommentRequest>(generatePath(PATHS.comments, { id }), {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.RequestComments, id])
      },
    },
    query: {
      enabled: !!id,
    },
  })
}

export const useGetComments = (id?: string | number) => {
  return useGetList<Comment>([RequestsKeys.RequestComments, id], generatePath(PATHS.comments, { id }), {
    query: {
      enabled: !!id,
    },
  })
}

export const useRemindApprovers = (id: string | number) => {
  const cache = useQueryClient()
  return usePatch(generatePath(PATHS.remind, { id }), {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Request, id])
      },
    },
  })
}

export interface ForwardRecipient {
  id: number
  full_name: string
  email: string
}

export interface ForwardRecipientsResponse {
  recipients: ForwardRecipient[]
}

export const useGetForwardRecipients = (id?: string | number) => {
  return useGetOne<ForwardRecipientsResponse>(
    [RequestsKeys.ForwardRecipients, id],
    generatePath(PATHS.forward, { id }),
    {
      query: {
        enabled: !!id,
        staleTime: 1 * 60 * 1000,
      },
    },
  )
}

export interface ForwardRequestPayload {
  recipients: number[]
  message?: string
}

export interface ForwardRequestResponse {
  id: string
  message: string
  recipients_count: number
}

export const useForwardRequest = (id: string | number) => {
  const cache = useQueryClient()
  return useMutation<ForwardRequestResponse, AxiosError<{ error: string }>, ForwardRequestPayload>(
    async (payload: ForwardRequestPayload) => {
      const path = generatePath(PATHS.forward, { id })
      const res = await api.post<ForwardRequestResponse>(path, payload)
      return res.data
    },
    {
      mutationKey: [RequestsKeys.ForwardRequest],
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Request, id])
        cache.invalidateQueries([RequestsKeys.Requests])
      },
    },
  )
}

// Tags
export const useGetTags = () => {
  return useGetList<Tag>([RequestsKeys.Tags], PATHS.tags, {
    query: {
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  })
}

export const useUpdateRequestTags = (requestId?: string | number) => {
  const cache = useQueryClient()
  return usePatch<{ tags: TagPayload[] }, ApprovalRequest>(`${PATHS.requests}${requestId}/tags`, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Request, requestId])
        cache.invalidateQueries([RequestsKeys.Requests])
        cache.invalidateQueries([RequestsKeys.Tags])
        cache.invalidateQueries([RequestsKeys.Filters])
      },
    },
  })
}

export const useCreateTag = () => {
  const cache = useQueryClient()
  return useMutation<Tag, AxiosError, TagPayload>(
    async (payload: TagPayload) => {
      const res = await api.post<Tag>(PATHS.tags, payload)
      return res.data
    },
    {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Tags])
        cache.invalidateQueries([RequestsKeys.Filters])
      },
    },
  )
}

export const useUpdateTag = () => {
  const cache = useQueryClient()
  return useMutation<Tag, AxiosError, { id: number; name?: string; color?: string }>(
    async ({ id, ...payload }) => {
      const res = await api.patch<Tag>(`${PATHS.tags}/${id}`, payload)
      return res.data
    },
    {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Tags])
        cache.invalidateQueries([RequestsKeys.Requests])
        // Invalidate every cached request-detail query: any of them may
        // embed the tag we just renamed/recoloured.
        cache.invalidateQueries([RequestsKeys.Request])
        cache.invalidateQueries([RequestsKeys.Filters])
      },
    },
  )
}

export const useDeleteTag = () => {
  const cache = useQueryClient()
  return useMutation<void, AxiosError, number>(
    async (tagId: number) => {
      await api.delete(`${PATHS.tags}/${tagId}`)
    },
    {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Tags])
        cache.invalidateQueries([RequestsKeys.Requests])
        // Invalidate every cached request-detail query: deleting a tag
        // removes it from any request that referenced it.
        cache.invalidateQueries([RequestsKeys.Request])
        cache.invalidateQueries([RequestsKeys.Filters])
      },
    },
  )
}
